import PageHeader from 'components/general/pageHeader';
import React from 'react';
import { useSelector } from 'react-redux';
import { apiGet } from 'services';
import { Loading } from '@salesperformanceint/e4shared';
import ButtonGroup from 'components/prism/buttonGroup';
import CourseGroup from 'components/prism/courseGroup';
import EmptyState from './components/emptyState';

// CourseLibrary is a generic, kind-filtered library view. Pages that render it
// (e.g. views/courseLibraryRoleplayAi) pass which course kinds to show and a
// systemTextFunction that prefixes translation keys with the page's own namespace.
export const CourseLibrary = ({ kinds, systemTextFunction, profileId }) => {
    const [data, setData] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(false);
    const { user } = useSelector(state => state.user);
    const [option, setOption] = React.useState('available');

    React.useEffect(() => {
        if (user?.guid && kinds?.length) {
            loadLibraryData();
        }
    }, [user?.guid, kinds, profileId]);

    const loadLibraryData = () => {
        setLoading(true);
        setError(false);

        const kindQuery = kinds.map(kind => `kind=${encodeURIComponent(kind)}`).join('&');
        const path = profileId ? `course-library/${profileId}?${kindQuery}` : `course-library?${kindQuery}`;

        apiGet(
            path,
            user.guid,
            response => {
                const responseData = response || {};

                setData({
                    available: responseData.available || [],
                    completed: responseData.completed || [],
                });
                setLoading(false);
            },
            err => {
                console.error('Error loading course library data:', err);
                setError(true);
                setLoading(false);
            }
        );
    };

    const noResultsPrefix = profileId ? 'TeamMember/' : '';

    const text = {
        title: systemTextFunction('Heading/Title'),
        error: systemTextFunction('Error/Title'),
        errorSummary: systemTextFunction('Error/Summary'),
        retry: systemTextFunction('Error/Retry'),
        noKinds: systemTextFunction('NoKinds/Title'),
        noKindsSummary: systemTextFunction('NoKinds/Summary'),
        available: systemTextFunction('Button/Available'),
        completed: systemTextFunction('Button/Completed'),
        noAvailable: systemTextFunction(`${noResultsPrefix}NoAvailable/Title`),
        noAvailableSummary: systemTextFunction(`${noResultsPrefix}NoAvailable/Summary`),
        noCompleted: systemTextFunction(`${noResultsPrefix}NoCompleted/Title`),
        noCompletedSummary: systemTextFunction(`${noResultsPrefix}NoCompleted/Summary`),
    };

    const options = [
        { key: 'available', text: text.available },
        { key: 'completed', text: text.completed },
    ];

    return (
        <div id="course-library-page" data-testid="course-library-page">
            <PageHeader title={text.title} />

            {!kinds?.length && <EmptyState title={text.noKinds} message={text.noKindsSummary} />}

            {kinds?.length > 0 && (
                <>
                    <ButtonGroup options={options} option={option} setOption={setOption} loading={loading} />

                    {option === 'available' && data.available?.length > 0 && (
                        <div className="mt30" id="available-section">
                            <CourseGroup
                                title={text.available}
                                data={data.available}
                                managerView={!!profileId}
                                profileId={profileId}
                            />
                        </div>
                    )}

                    {option === 'completed' && data.completed?.length > 0 && (
                        <div className="mt30" id="completed-section">
                            <CourseGroup
                                title={text.completed}
                                data={data.completed}
                                managerView={!!profileId}
                                profileId={profileId}
                            />
                        </div>
                    )}

                    <section className="content mtm10">
                        {loading && <Loading />}

                        {error && (
                            <EmptyState
                                icon="exclamation-triangle"
                                title={text.error}
                                message={text.errorSummary}
                                action={
                                    <button className="btn btn-primary" onClick={loadLibraryData}>
                                        {text.retry}
                                    </button>
                                }
                            />
                        )}

                        {!loading && !error && option === 'available' && !data.available?.length && (
                            <EmptyState title={text.noAvailable} message={text.noAvailableSummary} />
                        )}

                        {!loading && !error && option === 'completed' && !data.completed?.length && (
                            <EmptyState title={text.noCompleted} message={text.noCompletedSummary} />
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

export default CourseLibrary;
